import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "./ui/Button";
import {
  CONSENT_EVENT,
  applyConsentScripts,
  getConsent,
  hasAnalyticsConsent,
  setConsent,
} from "../lib/consent";

export const OPEN_CONSENT_EVENT = "qst-open-consent";

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [analyticsOn, setAnalyticsOn] = useState(true);

  useEffect(() => {
    const existing = getConsent();
    if (existing) {
      applyConsentScripts(existing);
      setAnalyticsOn(Boolean(existing.analytics));
      setVisible(false);
      return;
    }
    setVisible(true);
  }, []);

  useEffect(() => {
    const onChange = () => {
      const next = getConsent();
      if (next) {
        setAnalyticsOn(Boolean(next.analytics));
        setVisible(false);
      }
    };
    const onOpen = () => {
      const existing = getConsent();
      if (existing) setAnalyticsOn(Boolean(existing.analytics));
      setVisible(true);
    };
    window.addEventListener(CONSENT_EVENT, onChange);
    window.addEventListener(OPEN_CONSENT_EVENT, onOpen);
    return () => {
      window.removeEventListener(CONSENT_EVENT, onChange);
      window.removeEventListener(OPEN_CONSENT_EVENT, onOpen);
    };
  }, []);

  const save = (analytics, advertising) => {
    const consent = setConsent({ analytics, advertising });
    applyConsentScripts(consent);
    setVisible(false);
  };

  const acceptAll = () => save(true, true);
  const rejectNonEssential = () => save(false, false);
  const savePreferences = () => save(analyticsOn, analyticsOn);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          role="dialog"
          aria-labelledby="cookie-consent-title"
          aria-describedby="cookie-consent-desc"
          initial={{ y: 40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 24, opacity: 0 }}
          transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
          className="fixed bottom-4 left-4 right-4 z-[9980] mx-auto max-w-xl rounded-2xl border border-zinc-200 bg-white p-5 shadow-xl shadow-zinc-900/10 md:bottom-6 md:left-6 md:right-auto md:w-[420px]"
        >
          <h2 id="cookie-consent-title" className="font-display text-lg font-bold text-brand-ink">
            We value your privacy
          </h2>
          <p id="cookie-consent-desc" className="mt-2 text-sm leading-relaxed text-brand-muted">
            We use cookies for essential site functions and, with your consent, analytics and advertising to
            improve our website. You can change your choice anytime. See our{" "}
            <Link to="/privacy" className="font-medium text-brand-purple hover:underline">
              Privacy Policy
            </Link>
            .
          </p>

          <label className="mt-4 flex cursor-pointer items-start gap-3 rounded-xl border border-zinc-100 bg-brand-surface px-3 py-3">
            <input
              type="checkbox"
              checked={analyticsOn}
              onChange={(e) => setAnalyticsOn(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-zinc-300 text-brand-purple focus:ring-brand-purple/40"
            />
            <span className="text-sm text-brand-muted">
              <span className="font-semibold text-brand-ink">Analytics &amp; advertising</span>
              <span className="mt-0.5 block">
                Helps us understand traffic and show relevant ads. No name or email is collected from browsing alone.
              </span>
            </span>
          </label>

          <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:flex-wrap">
            <Button
              type="button"
              onClick={acceptAll}
              className="h-10 flex-1 rounded-full bg-brand-purple px-4 text-white hover:bg-brand-purple-dark"
            >
              Accept all
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={savePreferences}
              className="h-10 flex-1 rounded-full px-4"
            >
              Save choice
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={rejectNonEssential}
              className="h-10 flex-1 rounded-full px-4 text-brand-muted"
            >
              Reject non-essential
            </Button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export function openCookieSettings() {
  window.dispatchEvent(new Event(OPEN_CONSENT_EVENT));
}

export function useAnalyticsConsent() {
  const [allowed, setAllowed] = useState(() => hasAnalyticsConsent());

  useEffect(() => {
    const sync = () => setAllowed(hasAnalyticsConsent());
    sync();
    window.addEventListener(CONSENT_EVENT, sync);
    return () => window.removeEventListener(CONSENT_EVENT, sync);
  }, []);

  return allowed;
}
